/**
 * AdminTaxonomies — admin pane for the per-project category catalogue
 * (Themes + Property Types) that powers the thematic filter row.
 *
 * Renders as a nested section inside FormsAdmin (see /plan-design-review
 * 2026-05-13, decision 4.1). The wrapper deliberately omits an h5 title
 * because FormsAdmin owns the section header.
 *
 * UI vocabulary follows the design-review decision to drop the
 * implementation-flavored word "chip" in favor of "Option" everywhere
 * the admin or contributor sees a label. The DB / API still use
 * `taxonomies` and `kind` — those are stable internals.
 *
 * Hex color values and "Not yet used" status are intentionally hidden
 * (design-review 4.1): the color is editable via the native swatch,
 * and unused options surface no caption rather than a noisy negative
 * status line.
 *
 * Mirrors the AdminProjectsManager / FormsAdmin shape:
 *   - Fetches the project list once on mount via adminProjectsManager.php.
 *   - Project picker scoped to the admin's permission set
 *     (isGlobalAdmin sees everything; mods see their own projects only).
 *   - For the active project, lists options via taxonomyManager.php?action=list
 *     with `with_counts=true` so we can show "in use by N places" and
 *     disable delete when that's > 0 to prevent surprise data loss.
 *   - Inline name/color edits hit taxonomyManager.php?action=update.
 *   - Add new option via taxonomyManager.php?action=create.
 *   - Delete via taxonomyManager.php?action=delete with a confirm dialog.
 *
 * Authz (closed 2026-06-08): the PHP endpoint now requires
 * require_project_admin on every write (create/update/delete) — the
 * `list` action stays public because the same chip catalogue is read
 * by the anonymous map's FilterBar (src/utils/taxonomy.js). All write
 * paths here go through apiCall() so the verified Firebase ID token
 * is attached automatically; 401/403 from the server falls through
 * to setStatusMsg as an "error" alert.
 *
 * The component still only mounts inside AdminDashboard's admin nav,
 * which is itself gated so non-admin users get redirected away from
 * /admin entirely.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiCall } from './api';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Server error message extractor that handles both axios-shaped errors
// (err.response.data.error) and apiCall's ApiError (err.body.error).
// Kept inline because we only consume it in three handlers and the shape
// shimming is unique to this file's mid-migration state.
function serverErrMsg(err, fallback) {
  return (
    err?.response?.data?.error ||
    err?.body?.error ||
    err?.message ||
    fallback
  );
}

// Admin section labels are plural for readability ("Themes", "Communities").
// The KEYS must mirror PHP TAXONOMY_KINDS / the `taxonomies.kind` enum
// (migrations 018 + 024). The FilterBar uses its own singular labels from
// KIND_META — admin wording is intentionally decoupled.
const TAXONOMY_KINDS = [
  { value: 'theme', label: 'Themes' },
  { value: 'property_type', label: 'Property Types' },
  { value: 'community', label: 'Communities' },
];

// Default color for new options. Same neutral-stone shade we use for the
// default theme palette in PHP/taxonomy_helpers.php so a freshly-added
// option doesn't visually clash with the seeded palette before the admin
// picks a final color.
const DEFAULT_NEW_COLOR = '#B5D4E8';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

function normalizeColor(v) {
  if (!v) return '';
  let s = String(v).trim();
  if (!s.startsWith('#')) s = `#${s}`;
  return s;
}

function AdminTaxonomies({ isGlobalAdmin, userProjectIds }) {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjectsId, setSelectedProjectsId] = useState('');

  const [taxonomies, setTaxonomies] = useState([]);
  const [taxonomiesLoading, setTaxonomiesLoading] = useState(false);

  const [statusMsg, setStatusMsg] = useState(null); // { kind: 'success' | 'error', text }

  // Per-row local edit drafts so users can fix typos before pressing Save
  // without spamming the API on every keystroke. Keyed by option id.
  const [drafts, setDrafts] = useState({});

  // New option form. Reset to defaults after every successful create.
  const [newOption, setNewOption] = useState({
    kind: 'theme',
    name: '',
    color: DEFAULT_NEW_COLOR,
  });

  // Confirm-delete dialog state. We require a click-through because
  // ON DELETE CASCADE removes every assignment in one shot.
  const [pendingDelete, setPendingDelete] = useState(null);

  // ── Project list (initial fetch) ──────────────────────────────────
  // adminProjectsManager.php?action=get stays a public endpoint (the
  // anonymous map needs it on initial render — see App.js routing), so
  // apiCall here is purely for the auth-attached request hygiene; the
  // call would succeed without a token too. Permission filtering is
  // still done client-side from the AuthContext userProjectIds so
  // project-scoped admins never see a Project dropdown listing
  // projects they can't actually edit.
  useEffect(() => {
    let cancelled = false;
    setProjectsLoading(true);
    apiCall('adminProjectsManager.php', {
      method: 'POST',
      body: { action: 'get' },
    })
      .then((body) => {
        if (cancelled) return;
        const list = Array.isArray(body?.projects) ? body.projects : [];
        const scoped = isGlobalAdmin
          ? list
          : list.filter((p) => userProjectIds.includes(p.projectid));
        setProjects(scoped);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('AdminTaxonomies: failed to load projects', err);
        setStatusMsg({ kind: 'error', text: serverErrMsg(err, 'Failed to load projects.') });
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isGlobalAdmin, userProjectIds]);

  // ── Reload options when the active project changes ────────────────
  const reloadTaxonomies = useCallback(
    async (projectsId) => {
      if (!projectsId) {
        setTaxonomies([]);
        return;
      }
      setTaxonomiesLoading(true);
      try {
        const body = await apiCall('taxonomyManager.php', {
          method: 'POST',
          body: {
            action: 'list',
            project_id: Number(projectsId),
            with_counts: true,
          },
        });
        const list = Array.isArray(body?.taxonomies) ? body.taxonomies : [];
        setTaxonomies(list);
        // Drop any stale drafts that no longer correspond to a real option.
        setDrafts({});
      } catch (err) {
        console.error('AdminTaxonomies: failed to load taxonomies', err);
        setStatusMsg({ kind: 'error', text: serverErrMsg(err, 'Failed to load options.') });
      } finally {
        setTaxonomiesLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    reloadTaxonomies(selectedProjectsId);
  }, [selectedProjectsId, reloadTaxonomies]);

  // ── Mutations ─────────────────────────────────────────────────────
  const handleCreate = useCallback(async () => {
    if (!selectedProjectsId) return;
    const name = (newOption.name || '').trim();
    if (!name) {
      setStatusMsg({ kind: 'error', text: 'Option name is required.' });
      return;
    }
    const color = normalizeColor(newOption.color);
    if (!HEX_RE.test(color)) {
      setStatusMsg({ kind: 'error', text: 'Pick a valid color.' });
      return;
    }
    try {
      const body = await apiCall('taxonomyManager.php', {
        method: 'POST',
        body: {
          action: 'create',
          project_id: Number(selectedProjectsId),
          kind: newOption.kind,
          name,
          color,
        },
      });
      if (body?.success) {
        setNewOption({ kind: newOption.kind, name: '', color: DEFAULT_NEW_COLOR });
        setStatusMsg({ kind: 'success', text: `Added "${name}".` });
        reloadTaxonomies(selectedProjectsId);
      } else {
        setStatusMsg({
          kind: 'error',
          text: body?.error || 'Failed to add option.',
        });
      }
    } catch (err) {
      setStatusMsg({ kind: 'error', text: serverErrMsg(err, 'Failed to add option.') });
    }
  }, [selectedProjectsId, newOption, reloadTaxonomies]);

  const handleSaveRow = useCallback(
    async (row) => {
      const draft = drafts[row.id];
      if (!draft) return;
      const name = (draft.name ?? row.name).trim();
      const color = normalizeColor(draft.color ?? row.color);
      if (!name) {
        setStatusMsg({ kind: 'error', text: 'Option name is required.' });
        return;
      }
      if (!HEX_RE.test(color)) {
        setStatusMsg({ kind: 'error', text: 'Pick a valid color.' });
        return;
      }
      try {
        const body = await apiCall('taxonomyManager.php', {
          method: 'POST',
          body: {
            action: 'update',
            id: row.id,
            project_id: row.project_id,
            name,
            color,
          },
        });
        if (body?.success) {
          setStatusMsg({ kind: 'success', text: `Saved "${name}".` });
          setDrafts((prev) => {
            const next = { ...prev };
            delete next[row.id];
            return next;
          });
          reloadTaxonomies(selectedProjectsId);
        } else {
          setStatusMsg({
            kind: 'error',
            text: body?.error || 'Failed to save option.',
          });
        }
      } catch (err) {
        setStatusMsg({ kind: 'error', text: serverErrMsg(err, 'Failed to save option.') });
      }
    },
    [drafts, reloadTaxonomies, selectedProjectsId],
  );

  const handleDelete = useCallback(
    async (row) => {
      try {
        const body = await apiCall('taxonomyManager.php', {
          method: 'POST',
          body: {
            action: 'delete',
            id: row.id,
            project_id: row.project_id,
          },
        });
        if (body?.success) {
          setStatusMsg({ kind: 'success', text: `Removed "${row.name}".` });
          reloadTaxonomies(selectedProjectsId);
        } else {
          setStatusMsg({
            kind: 'error',
            text: body?.error || 'Failed to delete option.',
          });
        }
      } catch (err) {
        setStatusMsg({ kind: 'error', text: serverErrMsg(err, 'Failed to delete option.') });
      } finally {
        setPendingDelete(null);
      }
    },
    [reloadTaxonomies, selectedProjectsId],
  );

  // ── Derived: group options by kind for the table render ───────────
  const groupedTaxonomies = useMemo(() => {
    const out = {};
    TAXONOMY_KINDS.forEach((k) => {
      out[k.value] = [];
    });
    taxonomies.forEach((t) => {
      if (out[t.kind]) out[t.kind].push(t);
    });
    return out;
  }, [taxonomies]);

  // ── Render ────────────────────────────────────────────────────────
  // Self-contained padding + max-width: AdminTaxonomies used to live
  // inside FormsAdmin's bottom wrapper which provided px:3 pb:4 and
  // maxWidth:1100. After the 2026-06-08 split into its own admin nav
  // item the wrapper went away, so we reproduce the spacing here so the
  // page never renders flush against the AdminDashboard chrome.
  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: 3, py: 3 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
        Themes &amp; Types
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Manage the filter options visitors use to narrow the map and that
        contributors attach to their submissions. New projects ship with
        a default set; edit, recolor, add, or remove options here per project.
      </Typography>

      {statusMsg && (
        <Alert
          severity={statusMsg.kind}
          sx={{ mb: 2 }}
          onClose={() => setStatusMsg(null)}
        >
          {statusMsg.text}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="taxonomy-project-select-label">Project</InputLabel>
          <Select
            labelId="taxonomy-project-select-label"
            label="Project"
            value={selectedProjectsId}
            onChange={(e) => setSelectedProjectsId(e.target.value)}
            disabled={projectsLoading}
          >
            <MenuItem value="">
              <em>— Select a project —</em>
            </MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.projectsid} value={p.projectsid}>
                {p.project_name} ({p.projectid})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {!selectedProjectsId ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Choose a project above to manage its options.
        </Typography>
      ) : taxonomiesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Add new option form. The color picker is the only color
              affordance — hex codes are intentionally hidden per the
              design review (4.1) since admins don't think in #RRGGBB. */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
              Add a new option
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'center' }}
            >
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="new-option-kind-label">Category</InputLabel>
                <Select
                  labelId="new-option-kind-label"
                  label="Category"
                  value={newOption.kind}
                  onChange={(e) =>
                    setNewOption((prev) => ({ ...prev, kind: e.target.value }))
                  }
                >
                  {TAXONOMY_KINDS.map((k) => (
                    <MenuItem key={k.value} value={k.value}>
                      {k.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Option name"
                value={newOption.name}
                onChange={(e) =>
                  setNewOption((prev) => ({ ...prev, name: e.target.value }))
                }
                inputProps={{ maxLength: 100 }}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <input
                type="color"
                value={newOption.color}
                onChange={(e) =>
                  setNewOption((prev) => ({ ...prev, color: e.target.value }))
                }
                style={{
                  width: 36,
                  height: 36,
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  background: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
                aria-label="New option color"
              />
              <Button variant="contained" onClick={handleCreate}>
                Add option
              </Button>
            </Stack>
          </Paper>

          {/* One section per kind so the visual grouping matches the
              FilterBar / wizard option picker. Empty kinds get a hint
              row instead of a blank section header. */}
          {TAXONOMY_KINDS.map(({ value: kind, label }) => {
            const rows = groupedTaxonomies[kind] || [];
            return (
              <Paper key={kind} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                  {label}
                </Typography>
                {rows.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No options yet. Add one above with category set to
                    "{label}".
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {rows.map((row) => {
                      const draft = drafts[row.id] || {};
                      const draftedName = draft.name ?? row.name;
                      const draftedColor = draft.color ?? row.color;
                      const isDirty =
                        draftedName !== row.name || draftedColor !== row.color;
                      const inUse = (row.place_count || 0) > 0;
                      return (
                        <Stack
                          key={row.id}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1.5}
                          alignItems={{ sm: 'center' }}
                          sx={{
                            p: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            background: '#fafafa',
                          }}
                        >
                          <input
                            type="color"
                            value={draftedColor}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [row.id]: {
                                  ...prev[row.id],
                                  color: e.target.value,
                                },
                              }))
                            }
                            style={{
                              width: 32,
                              height: 32,
                              border: '1px solid #ccc',
                              borderRadius: 4,
                              background: 'none',
                              padding: 0,
                              cursor: 'pointer',
                            }}
                            aria-label={`Color for ${row.name}`}
                          />
                          <TextField
                            size="small"
                            label="Name"
                            value={draftedName}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [row.id]: {
                                  ...prev[row.id],
                                  name: e.target.value,
                                },
                              }))
                            }
                            inputProps={{ maxLength: 100 }}
                            sx={{ flex: 1, minWidth: 200 }}
                          />
                          {/* Unused options show no caption — a "Not yet used"
                              status reads as a negative judgement on the
                              admin's pace. We surface a caption only when
                              the count is meaningful. */}
                          {inUse && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                minWidth: 110,
                                textAlign: { sm: 'right' },
                              }}
                            >
                              In use by {row.place_count} place
                              {row.place_count === 1 ? '' : 's'}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={!isDirty}
                              onClick={() => handleSaveRow(row)}
                            >
                              Save
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => setPendingDelete(row)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            );
          })}
        </>
      )}

      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Delete this option?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDelete?.place_count > 0 ? (
              <>
                <strong>"{pendingDelete?.name}"</strong> is currently in use by{' '}
                {pendingDelete?.place_count} place
                {pendingDelete?.place_count === 1 ? '' : 's'}. Deleting it will
                remove the option from those places (the places themselves stay).
                This cannot be undone.
              </>
            ) : (
              <>
                This will permanently remove <strong>"{pendingDelete?.name}"</strong>{' '}
                from the project. This cannot be undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleDelete(pendingDelete)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminTaxonomies;
